import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OAuth2TokenResponse } from '../../shared/models/api.models';

@Injectable({
  providedIn: 'root',
})
export class PingFederateOAuthService {
  private http = inject(HttpClient);
  private config = environment.pingFederate;
  private tokenExchangeUrl = `${environment.apiUrl}/auth/pingfederate/token`;

  private getRedirectUri(): string {
    const hostname = window.location.hostname;
    const origin = window.location.origin;

    if (hostname === 'localhost') {
      return `${origin}/auth/callback`;
    }

    if (hostname.includes('azurestaticapps.net')) {
      return `${origin}/auth/callback`;
    }

    if (environment.production) {
      return `${origin}/auth/callback`;
    }

    return this.config.redirectUri || `${origin}/auth/callback`;
  }

  authorize(): void {
    sessionStorage.setItem('oauth_origin', window.location.origin);
    sessionStorage.setItem('oauth_provider', 'pingfederate');

    this.generatePKCEParams().then(({ codeVerifier, codeChallenge, state }) => {
      this.storeAuthParams(codeVerifier, state);
      const authUrl = this.buildAuthorizationUrl(codeChallenge, state);
      window.location.href = authUrl;
    });
  }

  handleCallback(code: string, state: string): Observable<OAuth2TokenResponse> {
    if (state !== this.getStoredState()) {
      throw new Error('Invalid state parameter');
    }

    const codeVerifier = this.getStoredCodeVerifier();
    if (!codeVerifier) {
      throw new Error('No code verifier found');
    }

    return this.exchangeCodeForToken(code, codeVerifier);
  }

  refreshAccessToken(refreshToken: string): Observable<OAuth2TokenResponse> {
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', this.config.clientId)
      .set('refresh_token', refreshToken)
      .set('scope', this.config.scope);

    return this.http.post<OAuth2TokenResponse>(this.tokenExchangeUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  }

  private buildAuthorizationUrl(codeChallenge: string, state: string): string {
    const params: Record<string, string> = {
      client_id: this.config.clientId,
      response_type: this.config.responseType,
      redirect_uri: this.getRedirectUri(),
      scope: this.config.scope,
      prompt: 'consent',
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: this.config.codeChallenge,
      response_mode: 'query',
    };

    const searchParams = new URLSearchParams(params);
    return `${this.config.authorizeUrl}?${searchParams.toString()}`;
  }

  private exchangeCodeForToken(
    code: string,
    codeVerifier: string
  ): Observable<OAuth2TokenResponse> {
    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('client_id', this.config.clientId)
      .set('code', code)
      .set('redirect_uri', this.getRedirectUri())
      .set('code_verifier', codeVerifier);

    return this.http.post<OAuth2TokenResponse>(this.tokenExchangeUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  }

  private async generatePKCEParams(): Promise<{
    codeVerifier: string;
    codeChallenge: string;
    state: string;
  }> {
    const codeVerifier = this.generateRandomString(128);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = this.generateRandomString(32);
    return { codeVerifier, codeChallenge, state };
  }

  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const values = crypto.getRandomValues(new Uint8Array(length));
    for (let i = 0; i < length; i++) {
      result += charset[values[i] % charset.length];
    }
    return result;
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const data = new TextEncoder().encode(codeVerifier);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(hashBuffer);
  }

  private base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private storeAuthParams(codeVerifier: string, state: string): void {
    sessionStorage.setItem('pf_code_verifier', codeVerifier);
    sessionStorage.setItem('pf_state', state);
  }

  private getStoredCodeVerifier(): string | null {
    return sessionStorage.getItem('pf_code_verifier');
  }

  private getStoredState(): string | null {
    return sessionStorage.getItem('pf_state');
  }

  clearAuthParams(): void {
    sessionStorage.removeItem('pf_code_verifier');
    sessionStorage.removeItem('pf_state');
    sessionStorage.removeItem('oauth_provider');
  }

  isOAuthCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') && urlParams.has('state');
  }

  getCallbackParams(): { code: string; state: string } | null {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    if (code && state) {
      return { code, state };
    }
    return null;
  }
}
